import { baseApiClient as api } from "../baseService";
export const addTagTypes = ["providers"] as const;
const injectedRtkApi = api
  .enhanceEndpoints({
    addTagTypes,
  })
  .injectEndpoints({
    endpoints: (build) => ({
      providersList: build.query<ProvidersListApiResponse, ProvidersListApiArg>(
        {
          query: () => ({ url: `/api/providers/` }),
          providesTags: ["providers"],
        },
      ),
      providersCreate: build.mutation<
        ProvidersCreateApiResponse,
        ProvidersCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/`,
          method: "POST",
          body: queryArg.createProviderRequest,
        }),
        invalidatesTags: ["providers"],
      }),
      providersRetrieve: build.query<
        ProvidersRetrieveApiResponse,
        ProvidersRetrieveApiArg
      >({
        query: (queryArg) => ({ url: `/api/providers/${queryArg.id}/` }),
        providesTags: ["providers"],
      }),
      providersUpdate: build.mutation<
        ProvidersUpdateApiResponse,
        ProvidersUpdateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateProviderRequest,
        }),
        invalidatesTags: ["providers"],
      }),
      providersContactsList: build.query<
        ProvidersContactsListApiResponse,
        ProvidersContactsListApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/contacts/`,
        }),
        providesTags: ["providers"],
      }),
      providersContactsSearch: build.query<
        ProvidersContactsSearchApiResponse,
        ProvidersContactsSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/contacts/search/`,
          params: {
            handle_type: queryArg.handleType,
            q: queryArg.q,
            unit_id: queryArg.unitId,
          },
        }),
        providesTags: ["providers"],
      }),
      providersSuggestedContact: build.query<
        ProvidersSuggestedContactApiResponse,
        ProvidersSuggestedContactApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/contacts/suggested/`,
          params: {
            handle_type: queryArg.handleType,
          },
        }),
        providesTags: ["providers"],
      }),
      providersUnitsList: build.query<
        ProvidersUnitsListApiResponse,
        ProvidersUnitsListApiArg
      >({
        query: (queryArg) => ({ url: `/api/providers/${queryArg.id}/units/` }),
        providesTags: ["providers"],
      }),
      providersUnitsCreate: build.mutation<
        ProvidersUnitsCreateApiResponse,
        ProvidersUnitsCreateApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/units/`,
          method: "POST",
          body: queryArg.createProviderUnitRequest,
        }),
        invalidatesTags: ["providers"],
      }),
      providersUpdateUnit: build.mutation<
        ProvidersUpdateUnitApiResponse,
        ProvidersUpdateUnitApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/units/${queryArg.unitId}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateProviderUnitRequest,
        }),
        invalidatesTags: ["providers"],
      }),
      providersAddUnitHandle: build.mutation<
        ProvidersAddUnitHandleApiResponse,
        ProvidersAddUnitHandleApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/units/${queryArg.unitId}/handles/`,
          method: "POST",
          body: queryArg.createProviderContactRequest,
        }),
        invalidatesTags: ["providers"],
      }),
      providersUpdateUnitHandle: build.mutation<
        ProvidersUpdateUnitHandleApiResponse,
        ProvidersUpdateUnitHandleApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/units/${queryArg.unitId}/handles/${queryArg.linkId}/`,
          method: "PATCH",
          body: queryArg.patchedUpdateProviderContactRequest,
        }),
        invalidatesTags: ["providers"],
      }),
      providersSetPrimaryHandle: build.mutation<
        ProvidersSetPrimaryHandleApiResponse,
        ProvidersSetPrimaryHandleApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/${queryArg.id}/units/${queryArg.unitId}/handles/${queryArg.linkId}/primary/`,
          method: "POST",
        }),
        invalidatesTags: ["providers"],
      }),
      providersMerge: build.mutation<
        ProvidersMergeApiResponse,
        ProvidersMergeApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/merge/`,
          method: "POST",
          body: queryArg.body,
        }),
        invalidatesTags: ["providers"],
      }),
      providersSearch: build.query<
        ProvidersSearchApiResponse,
        ProvidersSearchApiArg
      >({
        query: (queryArg) => ({
          url: `/api/providers/search/`,
          params: {
            q: queryArg.q,
          },
        }),
        providesTags: ["providers"],
      }),
    }),
    overrideExisting: false,
  });
export { injectedRtkApi as enhancedApi };
export type ProvidersListApiResponse = /** status 200  */ ProviderResponse[];
export type ProvidersListApiArg = void;
export type ProvidersCreateApiResponse = /** status 201  */ ProviderResponse;
export type ProvidersCreateApiArg = {
  createProviderRequest: CreateProviderRequest;
};
export type ProvidersRetrieveApiResponse = /** status 200  */ ProviderResponse;
export type ProvidersRetrieveApiArg = {
  id: string;
};
export type ProvidersUpdateApiResponse = /** status 200  */ ProviderResponse;
export type ProvidersUpdateApiArg = {
  id: string;
  patchedUpdateProviderRequest: UpdateProviderRequest;
};
export type ProvidersContactsListApiResponse =
  /** status 200  */ ContactHandleProviderUnitResponse[];
export type ProvidersContactsListApiArg = {
  id: string;
};
export type ProvidersContactsSearchApiResponse =
  /** status 200  */ ContactHandleProviderUnitResponse[];
export type ProvidersContactsSearchApiArg = {
  /** Optional handle type filter. */
  handleType?:
    | "EMAIL"
    | "FAX"
    | "MAILING_ADDRESS"
    | "OTHER"
    | "PHONE"
    | "WEBSITE";
  id: string;
  /** Search string applied to contact values and notes. */
  q?: string;
  /** Restrict search to a specific provider unit. */
  unitId?: string;
};
export type ProvidersSuggestedContactApiResponse =
  /** status 200  */ ContactSelectionResultResponse;
export type ProvidersSuggestedContactApiArg = {
  /** Optional handle type filter for selection. */
  handleType?:
    | "EMAIL"
    | "FAX"
    | "MAILING_ADDRESS"
    | "OTHER"
    | "PHONE"
    | "WEBSITE";
  id: string;
};
export type ProvidersUnitsListApiResponse =
  /** status 200  */ ProviderUnitResponse[];
export type ProvidersUnitsListApiArg = {
  id: string;
};
export type ProvidersUnitsCreateApiResponse =
  /** status 201  */ ProviderUnitResponse;
export type ProvidersUnitsCreateApiArg = {
  id: string;
  createProviderUnitRequest: CreateProviderUnitRequest;
};
export type ProvidersUpdateUnitApiResponse =
  /** status 200  */ ProviderUnitResponse;
export type ProvidersUpdateUnitApiArg = {
  id: string;
  unitId: string;
  patchedUpdateProviderUnitRequest: UpdateProviderUnitRequest;
};
export type ProvidersAddUnitHandleApiResponse =
  /** status 201  */ ContactHandleProviderUnitResponse;
export type ProvidersAddUnitHandleApiArg = {
  id: string;
  unitId: string;
  createProviderContactRequest: CreateProviderContactRequest;
};
export type ProvidersUpdateUnitHandleApiResponse =
  /** status 200  */ ContactHandleProviderUnitResponse;
export type ProvidersUpdateUnitHandleApiArg = {
  id: string;
  linkId: string;
  unitId: string;
  patchedUpdateProviderContactRequest: UpdateProviderContactRequest;
};
export type ProvidersSetPrimaryHandleApiResponse = unknown;
export type ProvidersSetPrimaryHandleApiArg = {
  id: string;
  linkId: string;
  unitId: string;
};
export type ProvidersMergeApiResponse = unknown;
export type ProvidersMergeApiArg = {
  body: {
    source_provider_id: string;
    target_provider_id: string;
  };
};
export type ProvidersSearchApiResponse = /** status 200  */ ProviderResponse[];
export type ProvidersSearchApiArg = {
  /** Search term applied to provider name or aliases. */
  q: string;
};
export type ContactHandleResponse = {
  id: number;
  handle_type: string;
  value: string;
  name?: string | null;
  notes?: string | null;
  archived_at?: string | null;
};
export type ContactHandleProviderUnitResponse = {
  id: string;
  provider_unit_id: string;
  contact_handle_id: number;
  handle: ContactHandleResponse;
  is_preferred: boolean;
  voice_ai_cadence_days?: number | null;
  capabilities: string;
  notes?: string | null;
  created_by_id?: string | null;
  updated_by_id?: string | null;
  created_by_agent?: string | null;
};
export type ProviderUnitResponse = {
  id: string;
  provider_id: string;
  unit_type: string;
  name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  notes?: string | null;
  archived_at?: string | null;
  handles?: ContactHandleProviderUnitResponse[];
};
export type ProviderResponse = {
  id: string;
  name: string;
  normalized_name: string;
  provider_type?: string | null;
  aliases?: string[];
  portal_eligible?: boolean;
  portal_type?: string | null;
  archived_at?: string | null;
  units?: ProviderUnitResponse[];
};
export type ProviderPortalType = "SMARTREQUEST" | "MRO" | "CHARTSWAP";
export type CreateProviderRequest = {
  name: string;
  normalized_name?: string | null;
  provider_type?: string | null;
  aliases?: string[] | null;
  portal_eligible?: boolean;
  portal_type?: ProviderPortalType;
};
export type UpdateProviderRequest = {
  name?: string | null;
  normalized_name?: string | null;
  provider_type?: string | null;
  aliases?: string[] | null;
  portal_eligible?: boolean | null;
  portal_type?: ProviderPortalType;
  archived_at?: string | null;
};
export type ContactSelectionResultResponse = {
  provider_unit_id: string;
  contact: ContactHandleProviderUnitResponse;
  reason: string;
};
export type ProviderContactUnitType =
  | "CATCH_ALL"
  | "FACILITY"
  | "DEPARTMENT"
  | "PRACTITIONER"
  | "BILLING"
  | "RECORDS";
export type CreateProviderUnitRequest = {
  unit_type: ProviderContactUnitType;
  name: string;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  notes?: string | null;
};
export type UpdateProviderUnitRequest = {
  unit_type?: ProviderContactUnitType;
  name?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  postal_code?: string | null;
  notes?: string | null;
  archived_at?: string | null;
};
export type ContactHandleType =
  | "PHONE"
  | "EMAIL"
  | "WEBSITE"
  | "FAX"
  | "OTHER"
  | "MAILING_ADDRESS";
export type WebSource = {
  /** The URL of the source */
  url: string;
  /** Very short display text for the source URL */
  title: string;
};
export type ContactHandleSchema = {
  id?: (string | string) | null;
  /** The captured contact information. Should contain an E.164 formatted phone number, or an email address, or a website, or a fax number. */
  value?: string | null;
  address_line1?: string | null;
  address_line2?: string | null;
  city?: string | null;
  state?: string | null;
  zip?: string | null;
  handle_type: ContactHandleType;
  /** Short description of the handle; e.g. 'Billing Dept Toll-Free', 'Front Desk Fax' etc. */
  name?: string | null;
  /** Optional notes about the handle. */
  notes?: string | null;
  /** Citations for the handle. */
  sources?: WebSource[] | null;
  /** Whether this is the preferred contact handle. */
  preferred?: boolean | null;
  division_id?: (string | string) | null;
};
export type ContactRequestCapabilities =
  | "RECORDS_ONLY"
  | "BILLS_ONLY"
  | "RECORDS_AND_BILLS";
export type AgentType =
  | "ingest_transcript_task"
  | "provider_finder"
  | "artifact_digest"
  | "treatment_researcher"
  | "treatment_request_automation"
  | "bland_caller"
  | "slack_task_bot"
  | "blob_sorter"
  | "front_app_integration"
  | "task_generator"
  | "coverage_extractor"
  | "intake_converter"
  | "airtable_sync"
  | "system"
  | "backfill_script"
  | "domain_event_update_orchestrator"
  | "loa_detector"
  | "rainmaker"
  | "deliverable_sync"
  | "bpo_agent"
  | "loa_chaser"
  | "external_task_rule_engine"
  | "external_task_bulk_create"
  | "entity_sync"
  | "case_activation";
export type CreateProviderContactRequest = {
  handle: ContactHandleSchema;
  is_preferred?: boolean;
  voice_ai_cadence_days?: number | null;
  capabilities?: ContactRequestCapabilities;
  notes?: string | null;
  created_by_agent?: AgentType;
};
export type UpdateProviderContactRequest = {
  is_preferred?: boolean | null;
  voice_ai_cadence_days?: number | null;
  capabilities?: ContactRequestCapabilities;
  notes?: string | null;
  created_by_agent?: AgentType;
};
export const {
  useProvidersListQuery,
  useProvidersCreateMutation,
  useProvidersRetrieveQuery,
  useProvidersUpdateMutation,
  useProvidersContactsListQuery,
  useProvidersContactsSearchQuery,
  useProvidersSuggestedContactQuery,
  useProvidersUnitsListQuery,
  useProvidersUnitsCreateMutation,
  useProvidersUpdateUnitMutation,
  useProvidersAddUnitHandleMutation,
  useProvidersUpdateUnitHandleMutation,
  useProvidersSetPrimaryHandleMutation,
  useProvidersMergeMutation,
  useProvidersSearchQuery,
} = injectedRtkApi;
